import FilterListIcon from '@mui/icons-material/FilterList'
import {
  Box,
  Chip,
  IconButton,
  Popover,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  styled,
} from '@mui/material'
import {
  Cell,
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  Header,
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import DatatablePagination from '../layout/DatatablePagination'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import ErrorAlert from '../error/ErrorAlert'
import { ErrorBoundary } from '../error/ErrorBoundary'
import TableSkeleton, { TableSkeletonProps } from '../loader/TableSkelton'
import FadeWrapper from '../transitions/FadeWrapper'
import Toolbar from '../toolbar/Toolbar'
import DataTableColumnFilter from './DataTableColumnFilter'
import DataTableColumnManager from './DataTableColumnManager'
import {
  DATA_TABLE_NOT_NULL_FILTER_VALUE,
  DATA_TABLE_NULL_FILTER_VALUE,
  DataTableActiveFilter,
  DataTableColumnMeta,
  DataTableComparisonFilterValue,
  DataTableFilterValue,
} from './DataTable.types'

interface DataTableProps<TData> {
  activeFilters?: DataTableActiveFilter[]
  animate?: boolean
  columnVisibility?: VisibilityState
  columns: ColumnDef<TData, any>[]
  data: TData[]
  darkHeader?: boolean
  defaultExpanded?: ExpandedState
  defaultSorting?: SortingState
  enableColumnFilters?: boolean
  enableColumnVisibility?: boolean
  enablePagination?: boolean
  enableRowSelection?: boolean
  enableToolbar?: boolean
  error?: Error
  footerTop?: boolean
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string
  loading?: boolean
  onClick?: (props: TData) => void
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>
  rowSelection?: RowSelectionState
  selectionCountLabelKey?: string
  selectionToolbar?: React.ReactNode
  setRowSelection?: OnChangeFn<RowSelectionState>
  skeletonProps?: Partial<TableSkeletonProps>
  stickyHeader?: boolean
  stickyTopPosition?: string
  containerSx?: SxProps<Theme>
  /** Only applies when `withContainer` is false. */
  scrollX?: boolean
  sx?: SxProps<Theme>
  toolbarEnd?: React.ReactNode
  toolbarStart?: React.ReactNode
  useGroupStyle?: boolean
  withContainer?: boolean
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'auto',
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& .MuiTableCell-root': {
    padding: theme.dataTable.standardCellPadding,
  },
  '& .MuiTableHead-root > .MuiTableRow-root:first-of-type .MuiTableCell-root': {
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    verticalAlign: 'middle',
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd) .MuiTableCell-root': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root': {
    backgroundColor: theme.dataTable.standardZebraBackground,
  },
  '& .MuiTableBody-root .MuiTableRow-hover:hover .MuiTableCell-root': {
    backgroundColor: `${theme.palette.header} !important`,
  },
  '& .MuiTableSortLabel-root': {
    gap: theme.spacing(0.25),
  },
  '& .MuiTableSortLabel-icon': {
    fontSize: 16,
    marginLeft: theme.spacing(0.25),
  },
  '@keyframes dataTableRowsIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  '& .DataTable-bodyTransition': {
    animation: `dataTableRowsIn ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
  },
}))

const darkHeaderStyle = {
  label: {
    backgroundColor: 'darkHeader',
    '& .MuiTableSortLabel-root': { color: 'white !important' },
    '& .MuiTableCell-head': { color: 'white !important' },
  },
  sortLabel: {
    '& .MuiTableSortLabel-icon': {
      color: 'white !important',
    },
  },
}

const resolveStateUpdate = <TState,>(updater: TState | ((old: TState) => TState), previous: TState): TState => {
  return typeof updater === 'function' ? (updater as (old: TState) => TState)(previous) : updater
}

const mergeSx = (baseSx: SxProps<Theme>, extraSx?: SxProps<Theme>): SxProps<Theme> => {
  if (!extraSx) return baseSx
  return [
    ...(Array.isArray(baseSx) ? baseSx : [baseSx]),
    ...(Array.isArray(extraSx) ? extraSx : [extraSx]),
  ] as SxProps<Theme>
}

const selectionColumnSx: SxProps<Theme> = {
  maxWidth: 45,
  textAlign: 'center',
  verticalAlign: 'middle',
  width: 45,
  '& > span': {
    justifyContent: 'center',
  },
  '& .MuiCheckbox-root': {
    p: 0,
  },
}

const getSelectionColumnSx = (columnId: string): SxProps<Theme> | undefined => {
  return columnId === 'selection' ? selectionColumnSx : undefined
}

const getHeaderContentSx = <TData,>(
  headerMetaSx: SxProps<Theme> | undefined,
  header: Header<TData, unknown>
): SxProps<Theme> => {
  return {
    alignItems: 'center',
    display: 'flex',
    gap: 0.5,
    justifyContent:
      header.column.id === 'selection'
        ? 'center'
        : getSxTextAlign(headerMetaSx) === 'right'
          ? 'flex-end'
          : 'flex-start',
    width: '100%',
  }
}

const isComparisonFilterValue = (filterValue: unknown): filterValue is DataTableComparisonFilterValue => {
  return (
    typeof filterValue === 'object' && filterValue !== null && !Array.isArray(filterValue) && 'operator' in filterValue
  )
}

const isUnrestrictedMultiSelectFilter = <TData,>(meta?: DataTableColumnMeta<TData>): boolean => {
  const filterValue = meta?.filterValue
  const options = meta?.filterOptions
  if (!Array.isArray(filterValue) || !options?.length) return false
  return options.every((option) => filterValue.includes(option.value))
}

export const getGroupStyle = <TData,>(
  cells: Header<TData, unknown>[] | Cell<TData, unknown>[],
  index: number
): SxProps<Theme> => {
  const currentGroup = (cells[index]?.column?.columnDef?.meta as DataTableColumnMeta<TData>)?.group
  const nextGroup = (cells[index + 1]?.column?.columnDef?.meta as DataTableColumnMeta<TData>)?.group
  if (!nextGroup || nextGroup !== currentGroup) return {}
  return { borderRight: '0 !important', borderLeft: '0 !important' }
}

const resolveHeaderSx = <TData,>(
  headerSx: DataTableColumnMeta<TData>['headerSx'],
  header: Header<TData, unknown>
): SxProps<Theme> | undefined => {
  if (typeof headerSx !== 'function') return headerSx
  return (headerSx as (header: Header<TData, unknown>) => SxProps<Theme>)(header)
}

const resolveCellSx = <TData,>(
  cellSx: DataTableColumnMeta<TData>['cellSx'],
  cell: Cell<TData, unknown>
): SxProps<Theme> | undefined => {
  if (typeof cellSx !== 'function') return cellSx
  return (cellSx as (cell: Cell<TData, unknown>) => SxProps<Theme>)(cell)
}

const getSxTextAlign = (sx?: SxProps<Theme>): unknown => {
  if (!sx || typeof sx !== 'object' || Array.isArray(sx)) return undefined
  return (sx as { textAlign?: unknown }).textAlign
}

const DataTable = <TData,>({
  activeFilters = [],
  animate = true,
  columnVisibility,
  columns,
  data,
  darkHeader = false,
  defaultExpanded = {},
  defaultSorting = [],
  enableColumnFilters = false,
  enableColumnVisibility = false,
  enablePagination = false,
  enableRowSelection = false,
  enableToolbar = false,
  error,
  footerTop = false,
  getRowId,
  loading = false,
  onClick,
  onColumnVisibilityChange,
  rowSelection = {},
  selectionCountLabelKey,
  selectionToolbar,
  setRowSelection,
  skeletonProps,
  stickyHeader = false,
  stickyTopPosition = '0px',
  containerSx,
  scrollX = false,
  sx,
  toolbarEnd,
  toolbarStart,
  useGroupStyle = false,
  withContainer = true,
}: DataTableProps<TData>): React.ReactElement => {
  const t = useAppTranslations()
  const [sorting, setSorting] = useState<SortingState>(defaultSorting)
  const [expanded, setExpanded] = useState<ExpandedState>(defaultExpanded)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>(columnVisibility ?? {})
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null)
  const [filteredColumnId, setFilteredColumnId] = useState<string>()

  const controlledColumnVisibility = columnVisibility ?? internalColumnVisibility
  const controlledRowSelection = enableRowSelection ? rowSelection : {}

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const nextVisibility = resolveStateUpdate(updater, controlledColumnVisibility)
    setInternalColumnVisibility(nextVisibility)
    onColumnVisibilityChange?.(nextVisibility)
  }

  const tableInstance = useReactTable({
    columns,
    data,
    defaultColumn: {
      filterFn: (row, columnId, filterValue) => {
        const value = row.getValue(columnId)
        if (filterValue === DATA_TABLE_NULL_FILTER_VALUE) return value === null || value === undefined
        if (filterValue === DATA_TABLE_NOT_NULL_FILTER_VALUE) return value !== null && value !== undefined
        if (Array.isArray(filterValue)) return filterValue.map(String).includes(String(value))
        if (isComparisonFilterValue(filterValue)) {
          if (value === null || value === undefined) return false
          const rowNumber = Number(value)
          const isNumericComparison =
            !Number.isNaN(rowNumber) &&
            (typeof filterValue.value === 'number' ||
              typeof filterValue.start === 'number' ||
              typeof filterValue.end === 'number' ||
              (!Number.isNaN(Number(filterValue.value)) &&
                !Number.isNaN(Number(filterValue.start ?? filterValue.value)) &&
                !Number.isNaN(Number(filterValue.end ?? filterValue.value))))

          if (isNumericComparison) {
            if (filterValue.operator === 'between') {
              const start = Number(filterValue.start)
              const end = Number(filterValue.end)
              if (Number.isNaN(start) || Number.isNaN(end)) return false
              return rowNumber >= start && rowNumber <= end
            }

            const comparisonNumber = Number(filterValue.value)
            if (Number.isNaN(comparisonNumber)) return false
            if (filterValue.operator === 'moreThan') return rowNumber > comparisonNumber
            if (filterValue.operator === 'moreThanOrEqual') return rowNumber >= comparisonNumber
            if (filterValue.operator === 'lessThan') return rowNumber < comparisonNumber
            if (filterValue.operator === 'lessThanOrEqual') return rowNumber <= comparisonNumber
            if (filterValue.operator === 'before') return rowNumber <= comparisonNumber
            return rowNumber >= comparisonNumber
          }

          const rowDate = dayjs(String(value))
          if (!rowDate.isValid()) return false
          if (filterValue.operator === 'between') {
            const start = dayjs(filterValue.start).startOf('day')
            const end = dayjs(filterValue.end).endOf('day')
            if (!start.isValid() || !end.isValid()) return false
            return rowDate.valueOf() >= start.valueOf() && rowDate.valueOf() <= end.valueOf()
          }

          const comparisonDate = dayjs(filterValue.value)
          if (!comparisonDate.isValid()) return false
          if (filterValue.operator === 'before') return rowDate.valueOf() <= comparisonDate.endOf('day').valueOf()
          return rowDate.valueOf() >= comparisonDate.startOf('day').valueOf()
        }
        return String(value ?? '')
          .toLowerCase()
          .includes(String(filterValue ?? '').toLowerCase())
      },
    },
    state: {
      columnFilters,
      columnVisibility: controlledColumnVisibility,
      expanded,
      rowSelection: controlledRowSelection,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onExpandedChange: setExpanded,
    onRowSelectionChange: enableRowSelection ? setRowSelection : undefined,
    onSortingChange: setSorting,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSubRows: (originalRow: any) => originalRow.subRows,
    getRowId: (originalRow: any, index, parent) => {
      if (getRowId) return getRowId(originalRow, index, parent)
      if (originalRow?.id !== undefined && originalRow?.id !== null) return String(originalRow.id)
      return parent ? `${parent.id}.${index}` : String(index)
    },
  })

  const hasFooter = useMemo(
    () =>
      tableInstance
        .getFooterGroups()
        .some((footerGroup) => footerGroup.headers.some((header) => header.column.columnDef.footer)),
    [tableInstance]
  )

  const filterableColumn = filteredColumnId ? tableInstance.getColumn(filteredColumnId) : undefined

  const getFilterValueLabel = (filterValue: DataTableFilterValue, meta?: DataTableColumnMeta<TData>): string => {
    if (Array.isArray(filterValue))
      return filterValue
        .map((value) => meta?.filterOptions?.find((option) => option.value === value)?.label ?? String(value))
        .join(', ')

    if (isComparisonFilterValue(filterValue)) {
      if (filterValue.operator === 'between')
        return `${t('table.comparisonFilter.between')} ${filterValue.start} - ${filterValue.end}`
      return `${t(`table.comparisonFilter.${filterValue.operator}`)} ${filterValue.value}`
    }

    const optionLabel = meta?.filterOptions?.find((option) => option.value === filterValue)?.label
    return optionLabel ?? (filterValue === null ? t('table.emptyValues') : String(filterValue))
  }

  const tableActiveFilters = useMemo<DataTableActiveFilter[]>(() => {
    const externalColumnFilters = tableInstance.getAllLeafColumns().flatMap<DataTableActiveFilter>((column) => {
      const meta = column.columnDef.meta as DataTableColumnMeta<TData> | undefined
      if (!meta?.onFilterChange || meta.filterValue === undefined) return []
      const label = meta.columnLabel ?? String(column.columnDef.header ?? column.id)

      if (Array.isArray(meta.filterValue)) {
        if (isUnrestrictedMultiSelectFilter(meta)) return []
        const allOptionValues = meta.filterOptions?.map((option) => option.value)
        return meta.filterValue.map<DataTableActiveFilter>((value, index) => ({
          id: `external-column-${column.id}-${index}`,
          label,
          onDelete: () => {
            const nextValues = (meta.filterValue as Array<string | number | boolean | null>).filter(
              (currentValue) => currentValue !== value
            )
            meta.onFilterChange?.(nextValues.length > 0 ? nextValues : allOptionValues)
          },
          value: getFilterValueLabel(value, meta),
        }))
      }

      return [
        {
          id: `external-column-${column.id}`,
          label,
          onDelete: () => meta.onFilterChange?.(undefined),
          value: meta.filterValueLabel ?? getFilterValueLabel(meta.filterValue, meta),
        },
      ]
    })

    const internalColumnFilters = columnFilters.flatMap<DataTableActiveFilter>((filter) => {
      const column = tableInstance.getColumn(filter.id)
      const meta = column?.columnDef.meta as DataTableColumnMeta<TData> | undefined
      const label = meta?.columnLabel ?? String(column?.columnDef.header ?? filter.id)
      const filterValue = filter.value

      if (Array.isArray(filterValue))
        return filterValue.map<DataTableActiveFilter>((value, index) => ({
          id: `column-${filter.id}-${index}`,
          label,
          onDelete: () => {
            const nextValues = filterValue.filter((currentValue) => currentValue !== value)
            column?.setFilterValue(nextValues.length > 0 ? nextValues : undefined)
          },
          value: getFilterValueLabel(value as DataTableFilterValue, meta),
        }))

      return [
        {
          id: `column-${filter.id}`,
          label,
          onDelete: () => column?.setFilterValue(undefined),
          value:
            filterValue === DATA_TABLE_NULL_FILTER_VALUE
              ? t('table.emptyValues')
              : filterValue === DATA_TABLE_NOT_NULL_FILTER_VALUE
                ? t('table.nonEmptyValues')
                : getFilterValueLabel(filterValue as DataTableFilterValue, meta),
        },
      ]
    })

    return [...activeFilters, ...externalColumnFilters, ...internalColumnFilters]
  }, [activeFilters, columnFilters, t, tableInstance])

  const tableBodyTransitionKey = useMemo(
    () =>
      [
        JSON.stringify(columnFilters),
        tableActiveFilters.map((filter) => `${filter.id}:${filter.value}`).join('|'),
      ].join('::'),
    [columnFilters, tableActiveFilters]
  )
  const selectedRowCount = Object.values(controlledRowSelection).filter(Boolean).length
  const enabledSelectionToolbar = enableRowSelection && selectedRowCount > 0 ? selectionToolbar : undefined
  const showToolbar = enableToolbar || Boolean(enabledSelectionToolbar)
  const toolbarContentKey = tableActiveFilters.map((filter) => `${filter.id}:${filter.value}`).join('|')

  const footer = tableInstance.getFooterGroups().map((footerGroup) => (
    <TableRow key={footerGroup.id}>
      {footerGroup.headers.map((header, index) => {
        const footerMeta = header.column.columnDef.meta as DataTableColumnMeta<TData> | undefined
        const footerMetaSx = typeof footerMeta?.cellSx === 'function' ? undefined : footerMeta?.cellSx
        return (
          <TableCell
            colSpan={header.colSpan}
            key={header.id}
            sx={mergeSx(useGroupStyle ? getGroupStyle(footerGroup.headers, index) : {}, footerMetaSx)}
          >
            {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
          </TableCell>
        )
      })}
    </TableRow>
  ))

  const table = (
    <Table size="small" sx={sx}>
      <TableHead sx={stickyHeader ? { position: 'sticky', top: stickyTopPosition, zIndex: 1 } : {}}>
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id} sx={darkHeader ? darkHeaderStyle.label : undefined}>
            {headerGroup.headers.map((header, index) => {
              const headerMeta = header.column.columnDef.meta as DataTableColumnMeta<TData> | undefined
              const headerMetaSx = resolveHeaderSx(headerMeta?.headerSx, header)
              const headerSelectionSx = getSelectionColumnSx(header.column.id)
              const canFilter = enableColumnFilters && header.column.getCanFilter() && headerMeta?.enableFilter
              const isFilterActive =
                header.column.getIsFiltered() ||
                (headerMeta?.filterValue !== undefined && !isUnrestrictedMultiSelectFilter(headerMeta))
              return (
                <TableCell
                  colSpan={header.colSpan}
                  key={header.id}
                  sx={mergeSx(
                    {
                      ...(useGroupStyle ? getGroupStyle(headerGroup.headers, index) : {}),
                      '&:focus-within .DataTable-filterButton, &:hover .DataTable-filterButton': {
                        opacity: 1,
                      },
                    },
                    mergeSx(headerMetaSx ?? {}, headerSelectionSx)
                  )}
                >
                  {header.isPlaceholder ? null : (
                    <Box component="span" sx={getHeaderContentSx(headerMetaSx, header)}>
                      {header.column.getCanSort() ? (
                        <Tooltip title={t('commons.sort')}>
                          <TableSortLabel
                            active={!!header.column.getIsSorted()}
                            direction={
                              header.column.getIsSorted() ? (header.column.getIsSorted() as 'asc' | 'desc') : undefined
                            }
                            onClick={header.column.getToggleSortingHandler()}
                            sx={{
                              '& .MuiTableSortLabel-icon': {
                                ...(darkHeader ? darkHeaderStyle.sortLabel['& .MuiTableSortLabel-icon'] : {}),
                                width: 16,
                              },
                            }}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </TableSortLabel>
                        </Tooltip>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                      {canFilter && (
                        <Tooltip title={t('commons.actions.filter')}>
                          <IconButton
                            color={isFilterActive ? 'primary' : 'default'}
                            onClick={(event) => {
                              event.stopPropagation()
                              setFilteredColumnId(header.column.id)
                              setFilterAnchor(event.currentTarget)
                            }}
                            className="DataTable-filterButton"
                            size="small"
                            sx={{
                              height: 20,
                              opacity: isFilterActive ? 1 : 0,
                              p: 0,
                              transition: (theme) =>
                                theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
                              width: 20,
                            }}
                          >
                            <FilterListIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </TableCell>
              )
            })}
          </TableRow>
        ))}
        {hasFooter && footerTop && tableInstance.getFooterGroups() && <>{footer}</>}
      </TableHead>
      <TableBody className="DataTable-bodyTransition" key={tableBodyTransitionKey}>
        {tableInstance.getRowModel().rows.length > 0 ? (
          tableInstance.getRowModel().rows.map((row) => (
            <TableRow hover key={row.id} sx={onClick ? { cursor: 'pointer ' } : {}}>
              {row.getVisibleCells().map((cell, index) => {
                const cellMeta = cell.column.columnDef.meta as DataTableColumnMeta<TData> | undefined
                const cellMetaSx = resolveCellSx(cellMeta?.cellSx, cell)
                const cellSelectionSx = getSelectionColumnSx(cell.column.id)
                return (
                  <TableCell
                    key={cell.id}
                    onClick={
                      onClick && cell.column.id !== 'actions' && cell.column.id !== 'selection'
                        ? () => onClick(row.original)
                        : undefined
                    }
                    sx={mergeSx(
                      useGroupStyle ? getGroupStyle(row.getVisibleCells(), index) : {},
                      mergeSx(cellMetaSx ?? {}, cellSelectionSx)
                    )}
                  >
                    {cell.getValue() === null && cell.column.columnDef.cell == null
                      ? 'n/a'
                      : flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                )
              })}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} sx={{ textAlign: 'center' }}>
              {t('commons.noDataToDisplay')}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {hasFooter && !footerTop && tableInstance.getFooterGroups() && <TableFooter>{footer}</TableFooter>}
      {enablePagination && (
        <DatatablePagination rowsDisplayed={data.length} colSpan={tableInstance.getFlatHeaders().length} />
      )}
    </Table>
  )

  const tableContent = loading ? (
    <TableSkeleton colNumbers={columns.length} darkHeader={darkHeader} {...skeletonProps} enableToolbar={false} />
  ) : (
    table
  )

  if (error && !loading) return <ErrorAlert message={error.message} />

  return (
    <ErrorBoundary>
      <FadeWrapper appear={animate}>
        <div>
          {showToolbar && (
            <Toolbar
              alwaysVisible={enableToolbar}
              contentKey={toolbarContentKey}
              end={
                <>
                  {toolbarEnd}
                  {enableColumnVisibility ? <DataTableColumnManager table={tableInstance} /> : null}
                </>
              }
              selectionActions={enabledSelectionToolbar}
              selectionCount={selectedRowCount}
              selectionCountLabelKey={selectionCountLabelKey}
              start={
                <>
                  {toolbarStart}
                  {tableActiveFilters.map((filter) => (
                    <Chip
                      key={filter.id}
                      label={filter.value ? `${filter.label}: ${filter.value}` : filter.label}
                      onDelete={filter.onDelete}
                      size="small"
                    />
                  ))}
                </>
              }
              style={darkHeader ? 'tableDarkHeader' : 'table'}
            />
          )}
          {withContainer ? (
            <StyledTableContainer sx={containerSx}>{tableContent}</StyledTableContainer>
          ) : scrollX ? (
            <Box data-testid="dataTableScroll" sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {tableContent}
            </Box>
          ) : (
            tableContent
          )}
          <Popover
            anchorEl={filterAnchor}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            onClose={() => setFilterAnchor(null)}
            open={Boolean(filterAnchor)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            {filterableColumn && (
              <DataTableColumnFilter column={filterableColumn} onClose={() => setFilterAnchor(null)} />
            )}
          </Popover>
        </div>
      </FadeWrapper>
    </ErrorBoundary>
  )
}

export default DataTable
